// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import {jsx} from 'panel';
import {selx} from 'clsx';

import {CellRendererFunc} from 'common/components/data-table/types';
import {formatMetricNumberWithUnits} from 'common/util';
import {LENGTH, SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {LIFT_TOOLTIP_COMPARE} from 'common/report/util/lift/types';
import {formatLiftPercent} from 'common/report/util/lift';
import {RetentionDataTableRow} from 'common/report/retention/util/types';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller';
import {ShowClause} from 'common/report/insights/util/types';
import {getFinalDisplayOptions, getMetricFormattingDefaultsFromShowClause} from 'common/report/insights/charts/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {MetricsResource} from 'common/report/model-resources/metrics';

export function getDataCellRenderer(
  hasLift: boolean,
  bookmarkController: RetentionBookmarkController,
  metricsResource: MetricsResource,
  metricKeyToShowClauseMap: Record<string, ShowClause>,
): CellRendererFunc<RetentionDataTableRow> {
  return function ({item, context}) {
    const cellIndex = parseInt(context.field, 10);
    const cell = item.cells.length > cellIndex ? item.cells[cellIndex] : null;

    if (cell) {
      const showClause = bookmarkController.getShowClauseFromRetentionTableRow(item, metricKeyToShowClauseMap);
      const isPct = bookmarkController.isPercentageView(item.matchingShowClauseIndex);
      const inputValue = isPct ? cell.percent * 100 : cell.value;
      const formatValue = (value) => {
        return formatMetricNumberWithUnits(
          value,
          getFinalDisplayOptions(
            showClause,
            eventPropertyDefinitionsStore,
            metricsResource,
            getMetricFormattingDefaultsFromShowClause(showClause),
          ),
        );
      };
      const mainDisplayValue = formatValue(inputValue);
      const coreCellContent = `${mainDisplayValue}${cell.incomplete ? `*` : ``}`;
      // TODO: We need to handle metric display options for the lift value case after the bug is fixed
      if (hasLift) {
        const liftField = item[context.liftField];
        const liftExtra = item[context.liftExtra];
        const isPositive = liftField > 0;
        const tooltipBaselineDisplayValue = formatValue(liftExtra);

        return (
          <>
            <div sel={selx(`mp-table-cell`)}>
              {coreCellContent}
              <TextHighlightEl
                sel='.mp-lift-pill'
                attrs={{
                  text: formatLiftPercent(liftField),
                  theme: isPositive ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE,
                  size: SIZE.SMALL,
                  length: LENGTH.THIN,
                }}
              />
              <TooltipEl
                attrs={{
                  placement: Placement.Top,
                  text: `${mainDisplayValue} ${LIFT_TOOLTIP_COMPARE} ${tooltipBaselineDisplayValue}`,
                }}
              />
            </div>
          </>
        );
      }
      return coreCellContent;
    }

    return undefined;
  };
}
